import axiosInstance from "../Middleware/interceptor";
// import { toast } from "react-toastify";

/**
 * Handles API errors consistently
 * @param error The error object
 */
const handleApiError = (error) => {
  let errorMessage = 'An unexpected error occurred';

  if (error.response) {
    // Server responded with a non-2xx status
    errorMessage = error.response.data?.message || error.response.statusText || 'Request failed';
  } else if (error.request) {
    // No response received
    errorMessage = 'No response from server. Please check your connection.';
  } else {
    // Setup error
    errorMessage = error.message;
  }

  // toast.error(errorMessage);
  return error;
};

/**
 * Handles API success responses consistently
 * @param response The API response
 * @param showSuccessToast Whether to show success toast
 */
const handleSuccessResponse = (response, showSuccessToast = true) => {
  // Handle both statusCode and status fields for compatibility
  const statusCode = response?.data?.statusCode;
  const status = response?.data?.status;
  
  if ((statusCode === 200 || status === true) && showSuccessToast) {
    // toast.success(response.data.message);
  } else if ((statusCode !== 200 && status !== true) && response?.data?.message) {
    // toast.error(response.data.message);
  }
  return response.data;
};

/**
 * POST request wrapper
 * @param url API endpoint
 * @param data Request payload
 * @param options Additional options including signal for AbortController
 * @returns Promise with response data
 */
export const postApi = async (url, data, options = {}) => {
  try {
    const { signal, ...otherOptions } = options;
    const config = {
      ...otherOptions,
      ...(signal && { signal })
    };
    
    const response = await axiosInstance.post(url, data, config);
    return handleSuccessResponse(response, options.showSuccessToast !== false);
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * GET request wrapper
 * @param url API endpoint
 * @param options Additional options including signal for AbortController
 * @returns Promise with response data
 */
export const getApi = async (url, options = {}) => {
  try {
    const { signal, ...otherOptions } = options;
    const config = {
      ...otherOptions,
      ...(signal && { signal })
    };
    
    const response = await axiosInstance.get(url, config);
    return handleSuccessResponse(response, options.showSuccessToast !== false);
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * PUT request wrapper
 * @param url API endpoint
 * @param data Request payload
 * @param options Additional options including signal for AbortController
 * @returns Promise with response data
 */
export const putApi = async (url, data, options = {}) => {
  try {
    const { signal, ...otherOptions } = options;
    const config = {
      ...otherOptions,
      ...(signal && { signal })
    };
    
    const response = await axiosInstance.put(url, data, config);
    return handleSuccessResponse(response, options.showSuccessToast !== false);
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * DELETE request wrapper
 * @param url API endpoint
 * @param data Request payload (optional)
 * @param options Additional options including signal for AbortController
 * @returns Promise with response data
 */
export const deleteApi = async (url, data = null, options = {}) => {
  try {
    const { signal, ...otherOptions } = options;
    const config = {
      ...otherOptions,
      ...(signal && { signal })
    };
    
    const deleteConfig = data ? { ...config, data } : config;
    const response = await axiosInstance.delete(url, deleteConfig);
    return handleSuccessResponse(response, options.showSuccessToast !== false);
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * PATCH request wrapper
 * @param url API endpoint
 * @param data Request payload
 * @param options Additional options including signal for AbortController
 * @returns Promise with response data
 */
export const patchApi = async (url, data, options = {}) => {
  try {
    const { signal, ...otherOptions } = options;
    const config = {
      ...otherOptions,
      ...(signal && { signal })
    };
    
    const response = await axiosInstance.patch(url, data, config);
    return handleSuccessResponse(response, options.showSuccessToast !== false);
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get top customers data
 * @returns Promise with top customers data
 */
export const getTopCustomers = async () => {
  try {
    const response = await getApi('/api/top-customers');
    return response;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get top customers data by month
 * @returns Promise with top customers data including all months sales
 */
export const getTopCustomersByMonth = async () => {
  try {
    const response = await getApi('/api/top-customers');
    return response;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get currencies data
 * @returns Promise with currencies data
 */
export const getCurrencies = async () => {
  try {
    const response = await getApi('/api/currencies');
    return response;
  } catch (error) {
    throw handleApiError(error);
  }
};